import type { OptionType } from '@services/optionService';
import type { Asset } from '@shared/types';

/**
 * Identifies a specific option contract
 */
export interface OptionIdentifier {
  readonly asset: Asset;
  readonly strikePrice: number;
  readonly type: OptionType;
  readonly expiryTimestamp: number;
}

/**
 * Option with performance metrics for home screen display
 */
export interface OptionListItem extends OptionIdentifier {
  readonly symbol: string; // e.g., "ETH2825+" or "BTC95000-"
  readonly currentPrice: number;
  readonly inceptionPrice: number;
  readonly priceChange: number;
  readonly priceChangePercent: number;
  readonly spotPrice: number;
}

/**
 * Data point for option price history chart
 */
export interface OptionPricePoint {
  readonly timestamp: number;
  readonly price: number;
  readonly spotPrice: number;
  readonly volatility: number;
}

/**
 * Available timeframe filters
 */
export type PerformanceTimeframe =
  | 'all'
  | '15m'
  | '30m'
  | '1h'
  | '2h'
  | '4h'
  | '8h'
  | '12h';

/**
 * Sort direction for option list
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Timeframe in milliseconds
 */
export const TIMEFRAME_MS: Record<PerformanceTimeframe, number> = {
  all: Infinity,
  '15m': 15 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '2h': 2 * 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  '8h': 8 * 60 * 60 * 1000,
  '12h': 12 * 60 * 60 * 1000,
};

/**
 * Format option as symbol string
 * @example formatOptionSymbol('ETH', 2825, 'CALL') => "ETH 2,825"
 * @example formatOptionSymbol('BTC', 95000, 'PUT') => "BTC 95,000"
 * @example formatOptionSymbol('XRP', 2.15, 'PUT') => "XRP 2.15"
 */
export function formatOptionSymbol(
  asset: Asset,
  strikePrice: number,
  _type: OptionType,
): string {
  // Format strike price with full precision using locale formatting
  const formattedStrike = strikePrice.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
  });

  return `${asset} ${formattedStrike}`;
}

/**
 * Create an OptionIdentifier from components
 */
export function createOptionIdentifier(
  asset: Asset,
  strikePrice: number,
  type: OptionType,
  expiryTimestamp: number,
): OptionIdentifier {
  return {
    asset,
    strikePrice,
    type,
    expiryTimestamp,
  };
}

/**
 * Check if two option identifiers refer to the same option
 */
export function isSameOption(
  a: OptionIdentifier,
  b: OptionIdentifier,
): boolean {
  return (
    a.asset === b.asset &&
    a.strikePrice === b.strikePrice &&
    a.type === b.type &&
    a.expiryTimestamp === b.expiryTimestamp
  );
}
